import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';

import { baseUrl } from '../../config';
import Modal from '../common/Modal';
import Button from '../common/Button';
import PageHeader from '../common/PageHeader';

class PasswordManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            users: [],
            loading: false,
            selectedUser: null,
            showSetPasswordModal: false,
            newPassword: '',
            confirmPassword: '',
            processing: false
        };
    }

    componentDidMount() {
        this.fetchUsers();
    }

    getAuthHeaders = () => {
        return {
            headers: { Authorization: 'Bearer ' + this.props.access_token }
        };
    };

    fetchUsers = async () => {
        this.setState({ loading: true });
        try {
            const response = await axios.get(baseUrl + 'api/user/admin/users', {
                params: { search: this.state.search },
                headers: { Authorization: 'Bearer ' + this.props.access_token }
            });
            this.setState({ users: response.data, loading: false });
        } catch (error) {
            toast.error('Failed to load users');
            this.setState({ loading: false });
        }
    };

    handleSearchChange = (e) => {
        this.setState({ search: e.target.value });
    };

    handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            this.fetchUsers();
        }
    };

    openSetPasswordModal = (user) => {
        this.setState({
            selectedUser: user,
            showSetPasswordModal: true,
            newPassword: '',
            confirmPassword: ''
        });
    };

    closeSetPasswordModal = () => {
        this.setState({
            selectedUser: null,
            showSetPasswordModal: false,
            newPassword: '',
            confirmPassword: ''
        });
    };

    handleSetPassword = async () => {
        const { selectedUser, newPassword, confirmPassword } = this.state;

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (newPassword.length < 9) {
            toast.error('Password must be at least 9 characters');
            return;
        }

        this.setState({ processing: true });
        try {
            await axios.post(baseUrl + 'api/user/admin/set-password', {
                userId: selectedUser.Id,
                newPassword: newPassword
            }, {
                headers: { Authorization: 'Bearer ' + this.props.access_token }
            });
            toast.success('Password set for ' + selectedUser.FirstName + ' ' + selectedUser.LastName + '. They will be required to change it on next login.');
            this.closeSetPasswordModal();
            this.fetchUsers();
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.Message) || (error.response && error.response.data) || 'Failed to set password';
            toast.error(message);
        } finally {
            this.setState({ processing: false });
        }
    };

    handleSendResetEmail = async (user) => {
        if (!user.EmailConfirmed) {
            toast.error('Cannot send reset email - user email is not confirmed');
            return;
        }

        this.setState({ processing: true });
        try {
            await axios.post(baseUrl + 'api/user/admin/send-reset-email', {
                userId: user.Id
            }, {
                headers: { Authorization: 'Bearer ' + this.props.access_token }
            });
            toast.success('Password reset email sent to ' + user.Email);
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.Message) || (error.response && error.response.data) || 'Failed to send reset email';
            toast.error(message);
        } finally {
            this.setState({ processing: false });
        }
    };

    formatDate = (dateString) => {
        if (!dateString) return '-';
        return moment(dateString).format('MM/DD/YYYY');
    };

    getPasswordStatus = (user) => {
        if (!user.PasswordExpiration) {
            return { text: 'No expiration', className: 'text-muted' };
        }
        var expDate = moment(user.PasswordExpiration);
        var now = moment();
        if (expDate.isBefore(now)) {
            return { text: 'Expired', className: 'text-danger' };
        }
        var daysLeft = expDate.diff(now, 'days');
        if (daysLeft <= 7) {
            return { text: 'Expires in ' + daysLeft + ' days', className: 'text-warning' };
        }
        return { text: 'Expires ' + expDate.format('MM/DD/YYYY'), className: 'text-success' };
    };

    render() {
        const { users, loading, search, showSetPasswordModal, selectedUser, newPassword, confirmPassword, processing } = this.state;

        return (
            <div className="container-fluid">
                <PageHeader title="Password Management" />

                <div className="card">
                    <div className="card-body">
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search users..."
                                        value={search}
                                        onChange={this.handleSearchChange}
                                        onKeyPress={this.handleSearchKeyPress}
                                    />
                                    <div className="input-group-append ml-2">
                                        <Button
                                            className="btn btn-primary"
                                            onClick={this.fetchUsers}
                                            disabled={loading}
                                        >
                                            <i className="fa fa-search" /> Search
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-4">
                                <i className="fa fa-spinner fa-spin fa-2x" />
                                <p className="mt-2">Loading users...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-striped table-hover" style={{ borderSpacing: '0 8px' }}>
                                    <thead>
                                        <tr>
                                            <th className="px-3">Name</th>
                                            <th className="px-3">Username</th>
                                            <th className="px-3">Email</th>
                                            <th className="px-3">Organization</th>
                                            <th className="px-3">Password Status</th>
                                            <th className="px-3">Account Status</th>
                                            <th className="px-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">
                                                    {search ? 'No users found matching your search' : 'Enter a search term to find users'}
                                                </td>
                                            </tr>
                                        ) : (
                                            users.map(user => {
                                                var pwStatus = this.getPasswordStatus(user);
                                                return (
                                                    <tr key={user.Id}>
                                                        <td className="px-3">{user.LastName}, {user.FirstName}</td>
                                                        <td className="px-3">{user.UserName}</td>
                                                        <td className="px-3">
                                                            {user.Email}
                                                            {!user.EmailConfirmed && (
                                                                <span className="badge badge-warning ml-2">Unconfirmed</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3">{user.Organization || '-'}</td>
                                                        <td className={'px-3 ' + pwStatus.className}>{pwStatus.text}</td>
                                                        <td className="px-3">
                                                            {user.IsLocked ? (
                                                                <span className="badge badge-danger">Locked</span>
                                                            ) : (
                                                                <span className="badge badge-success">Active</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3">
                                                            <Button
                                                                className="btn btn-outline-primary btn-sm"
                                                                onClick={() => this.openSetPasswordModal(user)}
                                                                disabled={processing}
                                                                title="Set a temporary password"
                                                            >
                                                                <i className="fa fa-key" /> Set Password
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {showSetPasswordModal && selectedUser && (
                    <Modal
                        show={showSetPasswordModal}
                        onHide={this.closeSetPasswordModal}
                        title={'Set Password for ' + selectedUser.FirstName + ' ' + selectedUser.LastName}
                    >
                        <div className="modal-body">
                            <p className="text-muted mb-3">
                                Set a temporary password for this user. They will be required to change it when they next log in.
                            </p>
                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={newPassword}
                                    onChange={(e) => this.setState({ newPassword: e.target.value })}
                                    placeholder="Enter new password (minimum 9 characters)"
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    value={confirmPassword}
                                    onChange={(e) => this.setState({ confirmPassword: e.target.value })}
                                    placeholder="Confirm new password"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button
                                className="btn btn-secondary"
                                onClick={this.closeSetPasswordModal}
                                disabled={processing}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="btn btn-primary"
                                onClick={this.handleSetPassword}
                                disabled={processing || !newPassword || !confirmPassword}
                            >
                                {processing ? <i className="fa fa-spinner fa-spin" /> : null}
                                {' '}Set Password
                            </Button>
                        </div>
                    </Modal>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    access_token: state.account.auth.access_token
});

export default connect(mapStateToProps)(PasswordManagement);
